import { FunctionComponent, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoltLightning } from '@fortawesome/pro-solid-svg-icons';
import { faArrowLeft, faXmark, faPlus } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { Radio } from 'components/UI/V3/Forms/Radio/Radio';
import Button from 'components/UI/V3/Button/Button';
import CriteriaPanel from '../Trigger/CriteriaPanel';
import AudiencePreviewModal from '../Trigger/AudiencePreviewModal';
import {
    type CriterionValue,
    type TriggerKind,
    type TriggerConfig,
    type FilterTriggerConfig,
    type ActionTriggerConfig,
    type SubjectField
} from '@spike/automation-model';
import { previewAudienceThunk, AutomationsStatus } from '@spike/automations-action';
import { useApiClientWrapper } from 'hooks';
import { useDebounce } from 'hooks/useDebounce';
import { RootState } from 'store';
import { buildCriterionChipLabel } from '../Trigger/criterionLabels';
import { useCriteriaCatalog } from '../../hooks/useCriteriaCatalog';
import { eventIcon } from '../Trigger/eventDefinitions';
import { useAutomationOptions } from '../../hooks/useAutomationOptions';
import { generateTimeOptions } from 'utils/TimeOptionsUtils';
import SchedulePanel, { emptyScheduleConfig } from './SchedulePanel';
import styles from './TriggerSidebar.module.scss';

const TIME_OPTIONS = generateTimeOptions(60, 0, 24, '12h', '24h');

// Automations saved before the switch carry "5:00pm", which matches no option.
const to24Hour = (value?: string): string => {
    if (!value) return '06:00';

    const match = value
        .trim()
        .toLowerCase()
        .match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/);
    if (!match) return '06:00';

    let hour = Number(match[1]);
    if (match[3] === 'pm' && hour < 12) hour += 12;
    if (match[3] === 'am' && hour === 12) hour = 0;

    return `${String(hour).padStart(2, '0')}:${match[2]}`;
};

const FREQ_OPTIONS = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'bimonthly', label: 'Bimonthly' },
    { value: 'semiannual', label: 'Semiannual' },
    { value: 'annual', label: 'Annual' }
];

type Page = 'type-select' | 'filter-config';
type CriteriaPanelMode =
    | 'client-filter'
    | 'event-conditions'
    | 'event-subject'
    | 'schedule-conditions'
    | 'schedule-subject'
    | null;

interface Props {
    initialConfig?: TriggerConfig;
    onSave?: (config: TriggerConfig) => void;
    onCancel?: () => void;
    onConfigChange?: (subtitle: string, chips?: string[]) => void;
}

const TriggerSidebar: FunctionComponent<Props> = ({ initialConfig, onSave, onCancel, onConfigChange }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [page, setPage] = useState<Page>(initialConfig ? 'filter-config' : 'type-select');
    const [triggerKind, setTriggerKind] = useState<TriggerKind | null>(initialConfig?.kind ?? null);
    const [scheduleConfig, setScheduleConfig] = useState(
        initialConfig?.kind === 'schedule' ? initialConfig : emptyScheduleConfig()
    );

    const [showCriteria, setShowCriteria] = useState<CriteriaPanelMode>(null);
    const [showAudienceModal, setShowAudienceModal] = useState(false);
    const filterConfig = initialConfig?.kind === 'filter' ? initialConfig : undefined;
    const actionConfig = initialConfig?.kind === 'action' ? initialConfig : undefined;
    const [criteria, setCriteria] = useState<CriterionValue[]>(filterConfig?.criteria ?? []);
    const [frequency, setFrequency] = useState(filterConfig?.frequency ?? '');
    const [time, setTime] = useState(to24Hour(filterConfig?.time));

    const [eventType, setEventType] = useState(actionConfig?.eventType ?? '');
    const [eventConditions, setEventConditions] = useState<CriterionValue[]>(actionConfig?.eventConditions ?? []);
    const [eventSubjectConditions, setEventSubjectConditions] = useState<CriterionValue[]>(
        actionConfig?.subjectConditions ?? []
    );

    // ── Audience preview (live count as criteria change) ───────────────────────
    const debouncedCriteria = useDebounce(criteria, 500);
    const { byField } = useCriteriaCatalog();
    const {
        groupedEvents,
        events: eventCatalog,
        anchors,
        subjectFieldsFor,
        subjectForAnchor,
        subjectForEvent
    } = useAutomationOptions();
    const audienceCount = useSelector((state: RootState) => state.automations.audiencePagination?.totalRecords ?? 0);
    const audienceLoading = useSelector(
        (state: RootState) => state.automations.status === AutomationsStatus.FetchingPreviewAudience
    );

    useEffect(() => {
        if (triggerKind !== 'filter' || debouncedCriteria.length === 0) return;
        (dispatch as any)(previewAudienceThunk(apiClientWrapper, debouncedCriteria, 1, 30));
    }, [debouncedCriteria, triggerKind]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Node subtitle sync ─────────────────────────────────────────────────────
    useEffect(() => {
        if (!onConfigChange) return;
        if (triggerKind === null) {
            onConfigChange('Select a trigger');
        } else if (triggerKind === 'action') {
            const eventDef = eventCatalog.find(e => e.type === eventType);
            const subtitle = eventDef?.label ?? 'Event trigger';
            const condCount = eventConditions.length;
            const chips = condCount > 0 ? [`${condCount} condition${condCount > 1 ? 's' : ''}`] : undefined;
            onConfigChange(subtitle, chips);
        } else if (triggerKind === 'schedule') {
            const anchor = anchors.find(a => a.key === scheduleConfig.anchor);
            const { amount, unit, direction } = scheduleConfig.offset;
            const subtitle = anchor ? `${amount} ${unit} ${direction} ${anchor.label.toLowerCase()}` : 'Date trigger';
            const chips = [
                ...scheduleConfig.conditions.map(c => buildCriterionChipLabel(c, byField)),
                ...scheduleConfig.subjectConditions.map(subjectChipLabel)
            ];
            onConfigChange(subtitle, chips.length > 0 ? chips : undefined);
        } else {
            const chips = criteria.map(c => buildCriterionChipLabel(c, byField));
            const freqLabel = frequency ? frequency.charAt(0).toUpperCase() + frequency.slice(1) : '';
            if (chips.length > 0) {
                onConfigChange(freqLabel || 'Scheduled trigger', chips);
            } else {
                onConfigChange('Scheduled trigger');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        triggerKind,
        criteria,
        frequency,
        eventType,
        eventConditions,
        eventSubjectConditions,
        scheduleConfig,
        onConfigChange
    ]);

    // ── Derived ───────────────────────────────────────────────────────────────
    const canConfirmType = triggerKind !== null;
    const canSaveScheduled = criteria.length > 0 && frequency !== '';
    const canSaveEvent = eventType !== '';
    const canSaveSchedule = scheduleConfig.anchor !== '';
    const canSave =
        triggerKind === 'action' ? canSaveEvent : triggerKind === 'schedule' ? canSaveSchedule : canSaveScheduled;

    const getLabelForCriterion = (c: CriterionValue) => buildCriterionChipLabel(c, byField);

    // Subject fields describe the record a trigger carries, so they come from
    // the options catalog rather than the criteria registry. Both trigger kinds
    // need the same shaping, hence the shared helpers.
    const toSubjectDefinition = (field: SubjectField) => ({
        field: field.key,
        label: field.label,
        description: '',
        category: 'business' as const,
        fieldType: field.fieldType,
        operators: field.operators,
        defaultOperator: field.operators[0],
        defaultValue: field.fieldType === 'multi_enum' ? [] : null,
        options: field.options ?? null,
        lookup: field.lookup ?? null,
        unit: null
    });

    const labelForSubjectCriterion = (c: CriterionValue, fields: SubjectField[]) => {
        const field = fields.find(f => f.key === c.field);
        if (!field) return c.field;

        const values = Array.isArray(c.value) ? c.value : [c.value];
        const labels = values.map(v => field.options?.find(o => o.value === String(v))?.label ?? String(v));

        return `${field.label}: ${labels.join(', ')}`;
    };

    const subjectFields = subjectFieldsFor(subjectForAnchor(scheduleConfig.anchor));
    const subjectDefinitions = subjectFields.map(toSubjectDefinition);
    const subjectChipLabel = (c: CriterionValue) => labelForSubjectCriterion(c, subjectFields);

    const eventSubjectFields = subjectFieldsFor(subjectForEvent(eventType));
    const eventSubjectDefinitions = eventSubjectFields.map(toSubjectDefinition);
    const eventSubjectChipLabel = (c: CriterionValue) => labelForSubjectCriterion(c, eventSubjectFields);

    const handleSave = () => {
        if (!canSave || !triggerKind) return;
        if (triggerKind === 'action') {
            onSave?.({
                kind: 'action',
                eventType,
                eventConditions,
                subjectConditions: eventSubjectConditions
            } as ActionTriggerConfig);
        } else if (triggerKind === 'schedule') {
            onSave?.(scheduleConfig);
        } else {
            onSave?.({ kind: 'filter', criteria, frequency, time } as FilterTriggerConfig);
        }
    };

    const stepTitle =
        triggerKind === 'action' ? 'Event Trigger' : triggerKind === 'schedule' ? 'Date Trigger' : 'Scheduled Trigger';

    return (
        <aside className={styles.sidebar}>
            {/* ── Criteria overlay (shared for both trigger types) ────────── */}
            {showCriteria === 'client-filter' && (
                <CriteriaPanel
                    title="Select criteria"
                    selected={criteria}
                    onBack={() => setShowCriteria(null)}
                    onConfirm={newCriteria => {
                        setCriteria(newCriteria);
                        setShowCriteria(null);
                    }}
                />
            )}
            {showCriteria === 'event-conditions' && (
                <CriteriaPanel
                    title="Add condition"
                    selected={eventConditions}
                    onBack={() => setShowCriteria(null)}
                    onConfirm={newConditions => {
                        setEventConditions(newConditions);
                        setShowCriteria(null);
                    }}
                />
            )}
            {showCriteria === 'event-subject' && (
                <CriteriaPanel
                    title="Conditions on the record"
                    selected={eventSubjectConditions}
                    definitions={eventSubjectDefinitions}
                    onBack={() => setShowCriteria(null)}
                    onConfirm={subjectConditions => {
                        setEventSubjectConditions(subjectConditions);
                        setShowCriteria(null);
                    }}
                />
            )}

            {showCriteria === 'schedule-conditions' && (
                <CriteriaPanel
                    title="Client conditions"
                    selected={scheduleConfig.conditions}
                    onBack={() => setShowCriteria(null)}
                    onConfirm={conditions => {
                        setScheduleConfig(prev => ({ ...prev, conditions }));
                        setShowCriteria(null);
                    }}
                />
            )}
            {showCriteria === 'schedule-subject' && (
                <CriteriaPanel
                    title="Conditions on the triggering record"
                    selected={scheduleConfig.subjectConditions}
                    definitions={subjectDefinitions}
                    onBack={() => setShowCriteria(null)}
                    onConfirm={subjectConditions => {
                        setScheduleConfig(prev => ({ ...prev, subjectConditions }));
                        setShowCriteria(null);
                    }}
                />
            )}

            {showAudienceModal && (
                <AudiencePreviewModal
                    criteria={criteria}
                    onClose={() => setShowAudienceModal(false)}
                />
            )}

            {/* ── PAGE 1: type selection ──────────────────────────────────── */}
            {page === 'type-select' && (
                <>
                    <div className={styles.body}>
                        <div className={styles.stepHeader}>
                            {onCancel && (
                                <div
                                    className={styles.backBtn}
                                    onClick={onCancel}
                                >
                                    <FontAwesomeIcon
                                        icon={faArrowLeft}
                                        fontSize={24}
                                        color="#00000"
                                    />
                                </div>
                            )}
                            <h3 className={styles.heading}>Select Trigger Type</h3>
                        </div>

                        <div
                            className={clsx(styles.typeCard, { [styles.selected]: triggerKind === 'filter' })}
                            onClick={() => setTriggerKind('filter')}
                        >
                            <Radio
                                checked={triggerKind === 'filter'}
                                onChange={() => setTriggerKind('filter')}
                                inlineLabel="Scheduled Trigger"
                                description="Run this workflow on a recurring schedule for clients who match selected criteria."
                            />
                        </div>

                        <div
                            className={clsx(styles.typeCard, { [styles.selected]: triggerKind === 'action' })}
                            onClick={() => setTriggerKind('action')}
                        >
                            <Radio
                                checked={triggerKind === 'action'}
                                onChange={() => setTriggerKind('action')}
                                inlineLabel="Event Trigger"
                                description="Run this workflow when a client performs a specific action or event."
                            />
                        </div>

                        <div
                            className={clsx(styles.typeCard, { [styles.selected]: triggerKind === 'schedule' })}
                            onClick={() => setTriggerKind('schedule')}
                        >
                            <Radio
                                checked={triggerKind === 'schedule'}
                                onChange={() => setTriggerKind('schedule')}
                                inlineLabel="Date Trigger"
                                description="Run this workflow a set time before or after a date — an appointment, a renewal, a birthday."
                            />
                        </div>
                    </div>

                    <div className={styles.footer}>
                        <Button
                            label="Confirm"
                            variant="green"
                            disabled={!canConfirmType}
                            onClick={() => canConfirmType && setPage('filter-config')}
                        />
                    </div>
                </>
            )}

            {/* ── PAGE 2: config ──────────────────────────────────────────── */}
            {page === 'filter-config' && (
                <>
                    <div className={styles.body}>
                        {/* Header */}
                        <div className={styles.stepHeader}>
                            <div
                                className={styles.backBtn}
                                aria-label="Go back"
                                onClick={() => setPage('type-select')}
                            >
                                <FontAwesomeIcon
                                    icon={faArrowLeft}
                                    fontSize={24}
                                    color="#00000"
                                />
                            </div>
                            <span className={styles.stepTitle}>{stepTitle}</span>
                        </div>

                        {/* Trigger icon row */}
                        <div className={styles.triggerRow}>
                            <div className={styles.triggerIconCircle}>
                                <FontAwesomeIcon icon={faBoltLightning} />
                            </div>
                            <div className={styles.triggerInfo}>
                                <span className={styles.triggerTitle}>Trigger</span>
                                <span className={styles.triggerDescription}>
                                    Clients enter the automation when this happens.
                                </span>
                            </div>
                        </div>

                        <hr className={styles.divider} />

                        {/* ── DATE TRIGGER config ───────────────────────── */}
                        {triggerKind === 'schedule' && (
                            <SchedulePanel
                                config={scheduleConfig}
                                onChange={setScheduleConfig}
                                onEditConditions={() => setShowCriteria('schedule-conditions')}
                                onEditSubjectConditions={() => setShowCriteria('schedule-subject')}
                                conditionChips={scheduleConfig.conditions.map(getLabelForCriterion)}
                                subjectConditionChips={scheduleConfig.subjectConditions.map(c => subjectChipLabel(c))}
                            />
                        )}

                        {/* ── SCHEDULED TRIGGER config ──────────────────── */}
                        {triggerKind === 'filter' && (
                            <>
                                {/* Clients / criteria */}
                                <div className={styles.section}>
                                    <div className={styles.sectionRow}>
                                        <div>
                                            <div className={styles.sectionLabel}>Clients</div>
                                            <div className={styles.sectionSubLabel}>
                                                Filter who can enter this automation
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.editButton}
                                            onClick={() => setShowCriteria('client-filter')}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                    {criteria.length > 0 && (
                                        <div className={styles.criteriaChips}>
                                            {criteria.map(c => (
                                                <div
                                                    key={c.id}
                                                    className={styles.criteriaChip}
                                                >
                                                    <span>{getLabelForCriterion(c)}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setCriteria(prev => prev.filter(x => x.id !== c.id))
                                                        }
                                                    >
                                                        <FontAwesomeIcon icon={faXmark} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <hr className={styles.divider} />

                                {/* Frequency */}
                                <div className={styles.section}>
                                    <div className={styles.frequencyLabel}>Check for matching contacts every</div>
                                    <div className={styles.frequencyRow}>
                                        <select
                                            className={styles.selectField}
                                            value={frequency}
                                            onChange={e => setFrequency(e.target.value)}
                                        >
                                            <option value="">Select</option>
                                            {FREQ_OPTIONS.map(o => (
                                                <option
                                                    key={o.value}
                                                    value={o.value}
                                                >
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                        <span className={styles.onLabel}>on</span>
                                        <select
                                            className={styles.timeField}
                                            value={time}
                                            onChange={e => setTime(e.target.value)}
                                        >
                                            {TIME_OPTIONS.map(t => (
                                                <option
                                                    key={String(t.value)}
                                                    value={String(t.value)}
                                                >
                                                    {t.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <hr className={styles.divider} />

                                {/* Stats */}
                                <div className={styles.section}>
                                    <div className={styles.statsRow}>
                                        <div className={styles.statsInfo}>
                                            <span className={styles.statsLabel}>Total clients</span>
                                            <span className={styles.statsValue}>
                                                {criteria.length === 0 ? 0 : audienceLoading ? '…' : audienceCount}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.editButton}
                                            disabled={criteria.length === 0}
                                            onClick={() => setShowAudienceModal(true)}
                                        >
                                            View Clients
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ── EVENT TRIGGER config ───────────────────────── */}
                        {triggerKind === 'action' && (
                            <>
                                {/* Event picker */}
                                <div className={styles.section}>
                                    <div className={styles.sectionLabel}>Event</div>
                                    <div className={styles.sectionSubLabel}>
                                        Select the action that fires this automation
                                    </div>

                                    <div className={styles.eventList}>
                                        {groupedEvents.map(group => (
                                            <div
                                                key={group.category}
                                                className={styles.eventGroup}
                                            >
                                                <div className={styles.eventGroupLabel}>{group.label}</div>
                                                {group.events.map(ev => (
                                                    <div
                                                        key={ev.type}
                                                        className={clsx(styles.eventRow, {
                                                            [styles.eventRowSelected]: eventType === ev.type
                                                        })}
                                                        onClick={() => setEventType(ev.type)}
                                                    >
                                                        <div
                                                            className={clsx(styles.eventIconCircle, {
                                                                [styles.eventIconSelected]: eventType === ev.type
                                                            })}
                                                        >
                                                            <FontAwesomeIcon icon={eventIcon(ev.type)} />
                                                        </div>
                                                        <div className={styles.eventInfo}>
                                                            <span className={styles.eventLabel}>{ev.label}</span>
                                                            <span className={styles.eventDesc}>{ev.description}</span>
                                                        </div>
                                                        <div
                                                            className={clsx(styles.eventRadioDot, {
                                                                [styles.eventRadioDotActive]: eventType === ev.type
                                                            })}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <hr className={styles.divider} />

                                {/* Conditions */}
                                <div className={styles.section}>
                                    <div className={styles.sectionRow}>
                                        <div>
                                            <div className={styles.sectionLabel}>Conditions</div>
                                            <div className={styles.sectionSubLabel}>
                                                Narrow down when this trigger fires
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.addConditionBtn}
                                            onClick={() => setShowCriteria('event-conditions')}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                            Add
                                        </button>
                                    </div>

                                    {eventConditions.length > 0 && (
                                        <div className={styles.criteriaChips}>
                                            {eventConditions.map(c => (
                                                <div
                                                    key={c.id}
                                                    className={styles.criteriaChip}
                                                >
                                                    <span>{getLabelForCriterion(c)}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setEventConditions(prev => prev.filter(x => x.id !== c.id))
                                                        }
                                                    >
                                                        <FontAwesomeIcon icon={faXmark} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {eventSubjectFields.length > 0 && (
                                    <div className={styles.section}>
                                        <div className={styles.sectionRow}>
                                            <div>
                                                <div className={styles.sectionLabel}>Conditions on the record</div>
                                                {/* Distinct from the block above: this asks about the record that
                                                    fired the event, not about the client's history. */}
                                                <div className={styles.sectionSubLabel}>
                                                    Only fire when the triggering record matches
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className={styles.addConditionBtn}
                                                onClick={() => setShowCriteria('event-subject')}
                                            >
                                                <FontAwesomeIcon icon={faPlus} />
                                                Add
                                            </button>
                                        </div>

                                        {eventSubjectConditions.length > 0 && (
                                            <div className={styles.criteriaChips}>
                                                {eventSubjectConditions.map(c => (
                                                    <div
                                                        key={c.id}
                                                        className={styles.criteriaChip}
                                                    >
                                                        <span>{eventSubjectChipLabel(c)}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setEventSubjectConditions(prev =>
                                                                    prev.filter(x => x.id !== c.id)
                                                                )
                                                            }
                                                        >
                                                            <FontAwesomeIcon icon={faXmark} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className={styles.footer}>
                        <Button
                            label="Save"
                            variant="green"
                            fullWidth
                            disabled={!canSave}
                            onClick={handleSave}
                        />
                    </div>
                </>
            )}
        </aside>
    );
};

export default TriggerSidebar;
